import { useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { QUOTED_PRODUCTS_PAGE_SIZE } from "../constants"
import { escapeIlikeWildcards } from "../utils"

import {
  GetQuotedProductListDocument,
  type GetQuotedProductListQuery,
  type GetQuotedProductListQueryVariables,
  type QuotedProductsQueryableFields,
} from "@/graphql/codegen/graphql"

// Narrowed to exactly what GetQuotedProductList selects — QuotedProductItem (the full codegen
// entity type) requires fields like salesPipelineId that this query doesn't fetch.
export type QuotedProductListItem = GetQuotedProductListQuery["salesPipeline"]["quotedProductsList"]["items"][number]

interface UseGetQuotedProductsParams {
  companyId: string
  query: string
  shipToLocationId?: string
}

interface UseGetQuotedProductsReturn {
  quotedProducts: QuotedProductListItem[]
  isFetching: boolean
  error: CombinedError | undefined
  loadMore: () => void
}

// Everything accumulated across pages for one queryKey — kept in a single object so a query-key
// change or a newly-committed page resets/updates all of it in one setState instead of several.
interface QuotedProductsPageState {
  queryKey: string
  offset: number
  quotedProducts: QuotedProductListItem[]
  lastCommittedData: GetQuotedProductListQuery | undefined
  total: number | undefined
  rawFetchedCount: number
  lastPageItemCount: number | undefined
}

const buildInitialPageState = (queryKey: string): QuotedProductsPageState => ({
  queryKey,
  offset: 0,
  quotedProducts: [],
  lastCommittedData: undefined,
  total: undefined,
  rawFetchedCount: 0,
  lastPageItemCount: undefined,
})

const buildQuotedProductsQueryKey = ({ companyId, query, shipToLocationId }: UseGetQuotedProductsParams): string =>
  `${companyId}::${query}::${shipToLocationId ?? ""}`

export const useGetQuotedProducts = ({
  companyId,
  query,
  shipToLocationId,
}: UseGetQuotedProductsParams): UseGetQuotedProductsReturn => {
  const queryKey = buildQuotedProductsQueryKey({ companyId, query, shipToLocationId })

  const [pageState, setPageState] = useState<QuotedProductsPageState>(() => buildInitialPageState(queryKey))

  if (pageState.queryKey !== queryKey) {
    setPageState(buildInitialPageState(queryKey))
  }

  const filter: QuotedProductsQueryableFields | undefined =
    query || shipToLocationId
      ? {
          query: query ? escapeIlikeWildcards(query) : undefined,
          shipToAssociationLocationId: shipToLocationId,
        }
      : undefined

  const variables: GetQuotedProductListQueryVariables = {
    input: { companyId, filter, offset: pageState.offset, limit: QUOTED_PRODUCTS_PAGE_SIZE },
  }

  const [{ data, fetching, error, operation }] = useQuery<GetQuotedProductListQuery>({
    query: GetQuotedProductListDocument,
    variables,
    pause: !companyId,
  })

  // urql keeps the previous result (and `operation`) visible while a new request for
  // different variables is in flight — comparing operation.variables to what this render
  // actually asked for stops a stale response from committing into a just-reset list.
  const isDataForCurrentQuery =
    data !== undefined && operation !== undefined && JSON.stringify(operation.variables) === JSON.stringify(variables)

  if (isDataForCurrentQuery && data !== pageState.lastCommittedData) {
    const newItems = data.salesPipeline.quotedProductsList.items
    const isFirstPage = pageState.offset === 0

    setPageState((prev) => ({
      ...prev,
      lastCommittedData: data,
      total: data.salesPipeline.quotedProductsList.pageInfo.total,
      lastPageItemCount: newItems.length,
      rawFetchedCount: isFirstPage ? newItems.length : prev.rawFetchedCount + newItems.length,
      quotedProducts: isFirstPage ? newItems : [...prev.quotedProducts, ...newItems],
    }))
  }

  const hasMoreByTotal = pageState.total === undefined || pageState.rawFetchedCount < pageState.total
  const hasFullLastPage =
    pageState.lastPageItemCount === undefined || pageState.lastPageItemCount === QUOTED_PRODUCTS_PAGE_SIZE
  const hasMore = hasMoreByTotal && hasFullLastPage

  const loadMore = () => {
    if (!hasMore || fetching) return
    setPageState((prev) => ({ ...prev, offset: prev.offset + QUOTED_PRODUCTS_PAGE_SIZE }))
  }

  return { quotedProducts: pageState.quotedProducts, isFetching: fetching, error, loadMore }
}
